import { BlockStack, InlineStack, Text, Icon, Tabs, Card, Divider, InlineGrid, Select, TextField, Checkbox, Autocomplete, Button, Popover, ActionList, Avatar, Link, ButtonGroup, IndexTable, Modal, Tag, ResourceList, OptionList, Spinner, Badge, Page, Banner } from "@shopify/polaris";
import { SearchIcon, PersonExitIcon, LogoInstagramIcon, LogoTiktokIcon, LogoYoutubeIcon, CheckIcon, ArrowLeftIcon, ExternalIcon, SendIcon, ImportIcon, PlusCircleIcon } from "@shopify/polaris-icons";
import { useEffect, useMemo, useState } from "react";

import { usePartnerUp } from "../providers/PartnerUpStoreProvider";
import AppLayout from "../components/layout";

import {
  languageCodes,
  interestCodes,
  countryCodes,
  weightsList
} from '../utils/search'
import { getFunctions, httpsCallable } from "firebase/functions";
import { getBestAvatar, shortenNumber } from "../utils";
import { useQuery } from "@tanstack/react-query";
import { doc, getDoc, setDoc } from "firebase/firestore";
import SearchProfile from "../components/creators/SearchProfile";
import { useNavigate } from "react-router-dom";
import AddCreatorModal from "../components/creators/AddCreatorModal";
import SearchTips from "../components/SearchTips";

const searchModash = httpsCallable(getFunctions(), 'searchModashV2');

// const languages = languageCodes.map(lang => lang.name)
// const interests = interestCodes.map(interest => interest.name)
const countries = countryCodes.map(country => country.name)

export default function Search() {

  const { store, someLoading, setToast, user, refetchUser, subscriptions, refetchLeads, getModashProfile, saveCreator, leads, auth, db, downloadsLimitReached } = usePartnerUp()
  const navigate = useNavigate()

  const [selectedPlatform, setSelectedPlatform] = useState(0);
  const [searchParams, setSearchParams] = useState({ hasEmail: true, sortBy: 'keywords' })
  const [weights, setWeights] = useState({})
  const [searchResults, setSearchResults] = useState([])

  const [savedSearchesOpen, setSavedSearchesOpen] = useState(false)
  const [savedSearches, setSavedSearches] = useState([])

  const [savingIndex, setSavingIndex] = useState(null)

  const [searchLoading, setSearchLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState([])
  const [viewLoading, setViewLoading] = useState(null)

  const [bulkBuckets, setBulkBuckets] = useState([])
  const [confirmSaveSelected, setConfirmSaveSelected] = useState(false)
  const [selectedSearchResults, setSelectedSearchResults] = useState([])

  const [activeProfileData, setActiveProfileData] = useState(null)

  const [addCreatorOpen, setAddCreatorOpen] = useState(false)

  const [keywordType, setKeywordType] = useState(0)

  const [autoSearch, setAutoSearch] = useState(false)

  const activeSub = subscriptions?.find(sub => sub.status === 'active')

  const credits = (user?.credits || 0) + (user?.fixedCredits || 0)

  const [tipsOpen, setTipsOpen] = useState(false)

  const tabs = useMemo(() => (
    <Tabs key="main-tabs" fitted tabs={[
      {
        id: 'instagram',
        content: (
          <InlineStack>
            <Icon source={LogoInstagramIcon} />
            <Text>Instagram</Text>
          </InlineStack>
        ),
      },
      {
        id: 'tiktok',
        content: (
          <InlineStack>
            <Icon source={LogoTiktokIcon} />
            <Text>TikTok</Text>
          </InlineStack>
        )
      },
      {
        id: 'youtube',
        content: (
          <InlineStack>
            <Icon source={LogoYoutubeIcon} />
            <Text>YouTube</Text>
          </InlineStack>
        )
      },
    ]} selected={selectedPlatform} onSelect={setSelectedPlatform} />
  ), [selectedPlatform])

  function closeTips() {
    if (!user?.seenSearchTips) {
      setDoc(doc(db, 'usersV2', auth.currentUser.uid), {
        seenSearchTips: true
      }, { merge: true })
    }
    setTipsOpen(false)
  }

  useEffect(() => {
    if (user) {
      setTipsOpen(!user?.seenSearchTips)
    }
  }, [user])

  useQuery({
    queryKey: ['lastSearch'],
    queryFn: async () => {
      console.log('running', auth?.currentUser?.uid, db)
      try {
        const lastSearch = await getDoc(doc(db, 'lastCreatorSearch', auth?.currentUser?.uid))
        if (!lastSearch.exists()) {
          console.log('filters', store?.filters)
          if (store?.filters) {
            if (store?.filters?.platform) {
              setSelectedPlatform(['instagram', 'tiktok', 'youtube'].indexOf(store?.filters?.platform))
            }
            setSearchParams(store.filters)
            setAutoSearch(true)
          }
          return true
        }
        const data = lastSearch.data()
        console.log('lastSearch', lastSearch.data())
        setSearchResults(data?.results)
        setSearchParams(data?.state)
        setWeights(data?.weights)
        return lastSearch.data()
      } catch (e) {
        console.error(e)
        throw e
      }
    },
    enabled: !!auth?.currentUser?.uid
  })

  useEffect(() => {
    if (autoSearch) {
      handleSearch()
      setAutoSearch(false)
    }
  }, [autoSearch])

  async function saveMultiple() {
    try {
      setSaveLoading(1000);

      const profiles = selectedSearchResults.map(userID => {
        const profile = searchResults?.lookalikes?.find(i => i.userId === userID)
        return {
          name: profile?.profile?.fullname,
          platform: profile?.platform,
          profileID: profile?.userId,
          shop: store.id,
        }
      })

      console.log('profiles', profiles)

      async function saveAll() {
        return new Promise((resolve) => {
          const interval = setInterval(async () => {
            const profile = profiles.pop()
            if (profile) {
              try {
                const { data } = await getModashProfile(profile)
                await saveCreator(data, bulkBuckets)
                setToast({
                  content: 'Saved ' + profile?.name,
                })
              } catch (e) {
                console.error(e)
                setToast({
                  content: 'Error saving ' + profile?.name,
                  error: true
                })
              }
            } else {
              clearInterval(interval)
              resolve()
            }
          }, 400)
        })
      }

      await saveAll()
      setConfirmSaveSelected(false)

    } catch (err) {
      setToast({
        content: 'Error Saving All Creators',
        error: true,
      })
      console.error(err);
    } finally {
      setSaveLoading(null);
    }
  }


  function handleSetSearchParams(key, value) {
    setSearchParams({ ...searchParams, [key]: value })
  }

  async function handleSearch(page = 0) {
    try {
      setSearchLoading(true)
      const platform = ['instagram', 'tiktok', 'youtube'][selectedPlatform]
      const results = await searchModash({
        platform,
        search: constructBody(page),
        state: searchParams,
        weights,
        prevPage: page > 0 ? searchResults : null,
        userID: auth.currentUser.uid
      })
      console.log('results', results?.data)
      setSearchResults(results?.data)
      if (results?.data?.warning) {
        setToast({
          content: '2 More Searches Today',
        })
      }
      // refetchStore()
    } catch (e) {
      console.error(e)
      setToast({ content: e.message, error: true })
    } finally {
      setSearchLoading(false)
    }
  }

  function constructBody(page = 0) {
    const body = {
      sort: {
        field: searchParams?.sortBy || 'keywords',
        value: 123,
        direction: 'desc'
      },
      page,
      filter: {
        influencer: {
          followers: (!searchParams.minFollowers || searchParams.minFollowers === 'Any') && (!searchParams.maxFollowers || searchParams?.maxFollowers === 'Any') ? null : {
            min: (searchParams.minFollowers && searchParams.minFollowers !== 'Any') ? Number(searchParams.minFollowers) : 0,
            max: (searchParams.maxFollowers && searchParams.maxFollowers !== 'Any') ? Number(searchParams.maxFollowers) : 10000000,
          },
          engagementRate: (searchParams.engagementRate && searchParams.engagementRate !== 'Any') ? Number(searchParams.engagementRate) : null,
          location: searchParams?.locations?.map(c => countryCodes.find(country => country.name === c)?.id) || null,
          language: (searchParams?.language && searchParams.language !== 'Any') ? languageCodes.find(l => l.name === searchParams?.language)?.code : null,
          lastposted: (searchParams.lastPosted && searchParams.lastPosted !== 'Any') ? Number(searchParams.lastPosted) : null,
          gender: (searchParams.gender && searchParams.gender !== 'Any') ? searchParams.gender?.toUpperCase() : null,
          age: (!searchParams.minAge || searchParams?.minAge === 'Any') && (!searchParams.maxAge || searchParams?.maxAge === 'Any') ? null : {
            min: Number(searchParams?.minAge) || 0,
            max: Number(searchParams.maxAge) || 100
          },
          hasContactDetails: searchParams.hasEmail ? [{
            contactType: 'email',
            filterAction: 'must'
          }] : null,
          interests: searchParams?.interests?.map(i => interestCodes.find(interest => interest.name === i)?.id) || null,
          bio: keywordType === 1 ? (searchParams.bio || null) : null,
          reelsPlays: selectedPlatform === 0 && (searchParams?.minReelsPlays || searchParams?.maxReelsPlays) ? {
            min: Number(searchParams.minReelsPlays) || 0,
            max: Number(searchParams.maxReelsPlays) || 50000000
          } : null,
          views: selectedPlatform !== 0 && (searchParams?.minReelsPlays || searchParams?.maxReelsPlays) ? {
            min: Number(searchParams.minReelsPlays) || 0,
            max: Number(searchParams.maxReelsPlays) || 50000000
          } : null,
          keywords: (selectedPlatform === 2 || keywordType === 0) ? (searchParams.keywords || null) : null,
          textTags: selectedPlatform === 2 ? null : keywordType === 2 ? (selectedPlatform === 0 ? (searchParams.hashtags?.split(' ') || [])?.map(hashtag => {
            const ht = hashtag?.replaceAll('#', '')?.replaceAll(',', '')
            return {
              type: 'hashtag',
              value: ht
            }
          })?.concat((searchParams.mentions?.split(' ') || [])?.map(mention => {
            const mt = mention?.replaceAll('@', '')?.replaceAll(',', '')
            return {
              type: 'mention',
              value: mt
            }
          }))?.filter(tt => {
            return Boolean(tt.value)
          }) : null) : null,
        },
        audience: {
          location: searchParams?.audienceLocations?.map(c => {
            return {
              id: countryCodes.find(country => country.name === c)?.id,
              weight: weights[c]
            }
          }) || null,
          language: (searchParams?.audienceLanguage && searchParams?.audienceLanguage !== 'Any') ? {
            id: languageCodes.find(l => l.name === searchParams?.audienceLanguage)?.code,
            weight: weights['audience-' + searchParams.audienceLanguage]
          } : null,
          gender: searchParams?.audienceGender ? {
            id: searchParams?.audienceGender.toUpperCase(),
            weight: weights[searchParams?.audienceGender]
          } : null,
          age: searchParams?.audienceAges?.map(age => {
            return {
              id: age,
              weight: weights[age]
            }
          }) || null,
          interests: searchParams?.audienceInterests?.map(i => {
            return {
              id: interestCodes.find(interest => interest.name === i)?.id,
              weight: weights[i]
            }
          }) || null,
          credibility: (searchParams?.fakeFollowers && searchParams?.fakeFollowers !== 'Any') ? (100 - Number(searchParams.fakeFollowers.replace('%', '').replace('<'))) / 100 : null
        }
      }
    }
    // delete null value fields
    for (let key in body.filter.audience) {
      if (body.filter.audience[key] === null || body.filter.audience[key].length === 0) {
        delete body.filter.audience[key]
      }
    }
    for (let key in body.filter.influencer) {
      if (body.filter.influencer[key] === null || body.filter.influencer[key].length === 0) {
        delete body.filter.influencer[key]
      }
    }

    console.log('body is', body)

    return body
  }

  const rowMarkup = (searchResults?.lookalikes || []).filter((row, key) => {
    // return hiddenCreators[row.userId] !== true
    return true
  }).map((row, i) => {
    const atName = row.profile?.handle || row.profile?.username
    const userID = row?.userId

    console.log({ leads })

    const tableRow = (
      <IndexTable.Row
        key={i}
        position={i}
        id={userID}
        selected={selectedSearchResults?.includes(userID)}
      >
        <IndexTable.Cell>
          <InlineStack gap="300">
            <img width="50px" src={row.profile?.picture} />
            <Text removeUnderline onClick={async (e) => {
              /*
              e.stopPropagation()
              if (!credits) {
                setToast({
                  content: 'Out of Credits',
                  error: true
                })
                return
              }
              try {
                setViewLoading(i)
                const fullCreator = leads?.find((lead) => lead.profile?.userId === userID)
                if (fullCreator) {
                  navigate(`/creators/${fullCreator.id}`)
                } else {
                  const { data: profile } = await getModashProfile({
                    shop: store.id,
                    profileID: userID,
                    platform: searchResults?.platform,
                  })
                  setActiveProfileData(profile)
                }
              } catch (e) {
                console.error(e)
                setToast({
                  content: 'Error loading profile',
                  error: true
                })
              } finally {
                setViewLoading(null)
              }
*/
            }}>
              <BlockStack align="center">
                <InlineStack gap="200">
                  <Text variant="headingSm">
                    {
                      leads?.find((lead) => lead?.handle === atName) ? (
                        <Link removeUnderline onClick={(e) => {
                          e.stopPropagation()
                          navigate('/creators/' + leads.find(lead => lead.handle === row.profile?.username).id)
                        }}>{row.profile?.fullname}</Link>
                      ) : (
                        row.profile?.fullname
                      )
                    }
                  </Text>
                </InlineStack>
                <InlineStack blockAlign="center" align="start">
                  {
                    searchResults?.platform === 'instagram' ? (
                      <InlineStack>
                        <Icon source={LogoInstagramIcon} />
                      </InlineStack>
                    ) : searchResults?.platform === 'tiktok' ? (
                      <InlineStack>
                        <Icon source={LogoTiktokIcon} />
                      </InlineStack>
                    ) : searchResults?.platform === 'youtube' ? (
                      <InlineStack>
                        <Icon source={LogoYoutubeIcon} />
                      </InlineStack>
                    ) : null
                  }
                  <Link removeUnderline target="_blank" url={`https://${searchResults?.platform}.com/${searchResults?.platform === 'instagram' ? '' : '@'}${atName}`}>{`@${atName}`}</Link></InlineStack>
              </BlockStack>
            </Text>
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <Text variant="bodyLg">{shortenNumber(row.profile?.followers)}</Text>
        </IndexTable.Cell>
        {
          searchResults?.platform !== 'instagram' ? (
            <IndexTable.Cell>
              <Text variant="bodyLg">
                {shortenNumber(row.profile?.averageViews)}
              </Text>
            </IndexTable.Cell>
          ) : (
            null
          )
        }
        <IndexTable.Cell>
          <Text variant="bodyLg">
            {(row.profile?.engagementRate * 100)?.toFixed(2) + '%'}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>
          <ButtonGroup>
            {
              leads?.find((lead) => lead?.handle === atName) ? (
                <InlineStack gap="100">
                  <Icon color="success" source={CheckIcon} />
                  <Text color="success">Saved</Text>
                </InlineStack>
              ) : (
                <InlineStack gap="200">
                  <Button size="slim" loading={saveLoading.includes(i)} onClick={async (e) => {
                    e.stopPropagation()
                    setToast({
                      content: 'Fetching Stats, Using 1 Credit',
                    })
                    setSavingIndex(i)
                    setSaveLoading([...saveLoading, i])
                    const { data } = await getModashProfile({
                      platform: searchResults?.platform,
                      username: row.profile?.username || row.profile?.handle,
                      isExtension: false
                    })
                    await saveCreator(data, bulkBuckets)
                    refetchLeads()
                    refetchUser()
                    setSaveLoading(saveLoading.filter(i => i !== i))
                    setSavingIndex(null)
                  }} disabled={!credits}>Get Stats</Button>
                </InlineStack>
              )
            }
          </ButtonGroup>
        </IndexTable.Cell>
      </IndexTable.Row>
    )

    return tableRow
  })

  if (activeProfileData) {
    return (
      <AppLayout>
        <Button size="large" icon={ArrowLeftIcon} onClick={() => navigate(-1)}>Back</Button>
        <Page
          // breadcrumbs={[{ content: 'Back', onAction: backAction }]}
          fullWidth
          title={<InlineStack gap="200" align="start">
            <InlineStack gap="200" blockAlign="center">
              <Avatar shape="square" source={getBestAvatar(activeProfileData)} size="xl" />
              <BlockStack>
                <InlineStack>
                  <InlineStack>
                    {
                      activeProfileData?.platform === 'instagram' ? (
                        <Icon source={LogoInstagramIcon} color="subdued" />
                      ) : activeProfileData?.platform === 'tiktok' ? (
                        <Icon source={LogoTiktokIcon} color="subdued" />
                      ) : activeProfileData?.platform === 'youtube' ? (
                        <Icon source={LogoYoutubeIcon} color="subdued" />
                      ) : null
                    }
                  </InlineStack>
                  <Link external removeUnderline url={`https://www.${activeProfileData?.platform}.com/${activeProfileData?.platform !== 'instagram' ? '@' + activeProfileData?.handle : activeProfileData?.handle}`}>
                    <Text variant="bodyLg">{activeProfileData?.handle ? `@${activeProfileData?.handle}` : activeProfileData?.platform}</Text>
                  </Link>
                  {/*
              <Text variant="bodyMd">
                {activeProfileData?.country ? `${getCountry(activeProfileData?.country)}${profileData?.city ? `, ${profileData.city} ` : ''}` : null}
              </Text>
            */}
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Text variant="headingLg">{activeProfileData?.name}</Text>
                </InlineStack>
              </BlockStack>
            </InlineStack>
          </InlineStack>}
          // open={activeProfileData?.userId}
          primaryAction={{
            content: 'Save Creator',
            loading: saveLoading === 1000,
            onAction: async () => {
              try {
                setSaveLoading(1000)
                const id = await saveCreator(activeProfileData)
                navigate('/creators/' + id)
              } catch (e) {
                console.error(e)
                setToast({
                  content: 'Error Saving Creator',
                  error: true
                })
              } finally {
                setSaveLoading(null)
              }

            },
          }}
          secondaryActions={
            [
              {
                content: `${activeProfileData?.platform ? activeProfileData?.platform[0]?.toUpperCase() + activeProfileData?.platform?.slice(1) : ''} Profile`,
                external: true,
                icon: ExternalIcon,
                url: `https://www.${activeProfileData?.platform}.com/${activeProfileData?.platform !== 'instagram' ? '@' + activeProfileData?.handle : activeProfileData?.handle}`
              },
            ]}
        >
          <SearchProfile profileData={activeProfileData} />
        </Page>
      </AppLayout>
    )
  }


  return (
    <AppLayout>
      <SearchTips
        close={closeTips}
        open={tipsOpen}
        countries={countries}
        searchParams={searchParams}
        handleSetSearchParams={handleSetSearchParams}
      />
      <AddCreatorModal saveCreator={saveCreator} open={addCreatorOpen} onClose={() => setAddCreatorOpen(false)} />
      <Modal
        open={confirmSaveSelected}
        onClose={() => setConfirmSaveSelected(false)}
        title={`Save ${selectedSearchResults?.length} Creators`}
        primaryAction={{
          content: 'Save All',
          loading: saveLoading === 1000,
          disabled: downloadsLimitReached,
          onAction: () => {
            saveMultiple()
          }
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setConfirmSaveSelected(false)
          }
        ]}
      >
        <Modal.Section>
          <InlineGrid gap="5" columns="1">
            <Card padding="0">
              <BlockStack>
                <ResourceList items={selectedSearchResults.map(userId => {
                  return searchResults?.lookalikes?.find(i => i.userId === userId)
                })
                } renderItem={(row, i) => {
                  const atName = row.profile?.handle || row.profile?.username
                  return (
                    <ResourceList.Item
                      key={i}
                      id={row.userId}
                      media={
                        <Avatar size="xl" source={row.profile?.picture} />
                      }
                    >
                      <BlockStack gap="1" align="center">
                        <Text variant="headingSm">{row.profile?.fullname}</Text>
                        {
                          atName ? (
                            <Text>{`@${atName}`}</Text>
                          ) : null
                        }
                      </BlockStack>
                    </ResourceList.Item>
                  )
                }} />
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="3">
                <BlockStack gap="2" inlineAlign="center">
                  <Popover active={savingIndex === 1000} onClose={() => setSavingIndex(null)} activator={
                    <Button disabled={saveLoading === 1000} size="slim" disclosure onClick={(e) => {
                      e.stopPropagation()
                      setSavingIndex(1000)
                    }}>Add to Buckets</Button>
                  }>
                    <OptionList allowMultiple options={[
                      ...(store?.buckets || [])?.map(bucket => {
                        return {
                          label: `${bucket?.emoji} ${bucket?.name}`,
                          value: bucket.id
                        }
                      })
                    ]}
                      onChange={(value) => {
                        setBulkBuckets(value)
                      }}
                      selected={bulkBuckets}
                    >Save</OptionList>
                  </Popover>
                  <InlineStack gap="1">
                    {
                      bulkBuckets?.map(bucketID => {
                        const bucket = store?.buckets?.find(i => i.id === bucketID)
                        return <Tag key={bucketID}>{bucket.emoji} {bucket.name}</Tag>
                      })
                    }
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>
          </InlineGrid>
          {
            downloadsLimitReached ? (
              <Banner title="Download Limit Reached" status="critical">
                <Text>Resets in {periodReset} Days. Upgrade Your Plan</Text>
              </Banner>
            ) : null
          }
        </Modal.Section>
      </Modal>
      <BlockStack gap="500">
        <Banner tone={credits || someLoading ? "info" : 'critical'} >
          {
            someLoading ? <Spinner size="small" /> : (
              <BlockStack gap="100">
                {
                  credits ? (
                    <Text variant="headingMd">
                      You have {credits} Credits {' '}
                    </Text>
                  ) : (
                    <Text variant="headingMd">
                      You're Out of Credits {' '}
                    </Text>
                  )
                }
                {
                  activeSub ? (
                    <Text variant="bodySm" tone="subdued">
                      You can {' '}
                      <Link url={
                        process.env.NODE_ENV === 'development'
                          ? "https://buy.stripe.com/test_bIY4hJ4hr7xhaY03ce?client_reference_id=" + auth.currentUser.uid
                          : "https://buy.stripe.com/cN2179ca9gfv6g87sw?client_reference_id=" + auth.currentUser.uid
                      }>Buy More</Link>
                      {' '} Or {' '}
                      <Link onClick={() => {
                        navigate('/settings')
                      }}>Upgrade Your Plan</Link>
                    </Text>
                  ) : (
                    <Text variant="bodySm" tone="subdued">
                      <Link onClick={() => {
                        navigate('/plan')
                      }}>Choose a Subscription</Link>
                      {' '} for More.
                    </Text>
                  )
                }
              </BlockStack>
            )
          }
        </Banner>
        <BlockStack gap="100">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="100">
              <InlineStack>
                <Icon source={SearchIcon} />
              </InlineStack>
              <Text variant="headingXl">
                Search
              </Text>
            </InlineStack>
            {
              /*
              <Button size="large" primary onClick={() => setAddCreatorOpen(true)} icon={PlusCircleIcon}>New Creator</Button>
              */
            }
            <Button variant="plain" icon={ExternalIcon} onClick={() => {
              setTipsOpen(true)
            }}>How to Search</Button>
          </InlineStack>
          {
            /*
            <Text>To add a single creator, visit the "Creators" tab</Text>
            */
          }
        </BlockStack>
        <Card padding="0">
          {tabs}
        </Card>
        <Text variant="headingLg">Keywords</Text>
        <Card background="bg">
          <Tabs tabs={selectedPlatform === 2 ? [{
            id: 'inVideo',
            content: 'Spoken Words in Video',
          }] : [
            {
              id: 'posts',
              content: 'Post Keywords',
            },
            {
              id: 'bio',
              content: 'Bio Keywords',
            },
            {
              id: 'mentions',
              content: 'Mentions',
            },
          ]} selected={keywordType} onSelect={i => {
            setKeywordType(i)
          }} />
          <InlineGrid columns={{ xs: "2", md: "3" }} gap="300">
            {
              keywordType === 0 ? (
                <TextField helpText="Words in Post captions, ranked by frequency" placeholder="Ex. Cozy Fashion" value={searchParams.keywords} onChange={v => handleSetSearchParams('keywords', v)} />
              ) : keywordType === 1 ? (

                <TextField helpText="Words in bio (more specific)" placeholder="Ex. Baseball" value={searchParams.bio} onChange={v => handleSetSearchParams('bio', v)} />
              ) : (

                <TextField helpText="Mentions, find tags of your brand, or competitors" placeholder="Ex. prada" value={searchParams.mentions} onChange={v => handleSetSearchParams('mentions', v)} />
              )
            }
          </InlineGrid>
        </Card>
        <Text variant="headingLg">Influencers</Text>
        <Card background="bg">
          <BlockStack gap="500">
            <InlineGrid columns={{ xs: "1", md: "4" }} gap="300">
              <BlockStack gap="100">
                <Text>Followers</Text>
                <InlineGrid columns="2" gap="300" align="start">
                  <Select labelInline label="From" options={[
                    { label: 'Any', value: 'Any' },
                    { label: '1,000', value: '1000' },
                    { label: '5,000', value: '5000' },
                    { label: '10,000', value: '10000' },
                    { label: '50,000', value: '50000' },
                    { label: '100,000', value: '100000' },
                    { label: '500,000', value: '500000' },
                    { label: '1,000,000', value: '1000000' },
                  ]}
                    value={searchParams.minFollowers}
                    onChange={(value) => handleSetSearchParams('minFollowers', value)} />
                  <Select labelInline label="To" options={[
                    { label: 'Any', value: 'Any' },
                    { label: '5,000', value: '5000' },
                    { label: '10,000', value: '10000' },
                    { label: '50,000', value: '50000' },
                    { label: '100,000', value: '100000' },
                    { label: '500,000', value: '500000' },
                    { label: '1,000,000', value: '1000000' },
                    { label: '5,000,000', value: '5000000' },
                  ]}
                    value={searchParams.maxFollowers}
                    onChange={(value) => handleSetSearchParams('maxFollowers', value)} />
                </InlineGrid>
              </BlockStack>
              <BlockStack gap="100">
                <Text>Age</Text>
                <InlineGrid columns="2" gap="300" align="start">
                  <Select labelInline label="From" options={[
                    { label: 'Any', value: 'Any' },
                    { label: '18', value: '18' },
                    { label: '25', value: '25' },
                    { label: '35', value: '35' },
                    { label: '45', value: '45' },
                  ]} value={searchParams.minAge} onChange={(value) => handleSetSearchParams('minAge', value)} />
                  <Select label="To" labelInline options={[
                    { label: 'Any', value: 'Any' },
                    { label: '18', value: '18' },
                    { label: '25', value: '25' },
                    { label: '35', value: '35' },
                    { label: '45', value: '45' },
                  ]} value={searchParams.maxAge} onChange={(value) => handleSetSearchParams('maxAge', value)} />
                </InlineGrid>
              </BlockStack>
              <Autocomplete
                allowMultiple
                onSelect={(value) => {
                  handleSetSearchParams('locations', [...(searchParams.locations || []), value.pop()])
                }}
                label="Country"
                options={
                  countries.map(c => ({ label: c, value: c })).filter(i => !(searchParams.locations?.includes(i.value) === true))
                }
                selected={searchParams.locations || []}
                textField={
                  <Autocomplete.TextField
                    placeholder={searchParams?.locations?.length > 0 ? "Add Location" : "Any"}
                    label="Country"
                    value={''}
                    autoComplete="off"
                    verticalContent={
                      searchParams?.locations?.length > 0 ? (
                        <InlineStack gap="300" align="start">
                          {
                            searchParams.locations?.map(interest => {
                              return (
                                <Tag key={interest} onRemove={
                                  () => handleSetSearchParams('locations', searchParams.locations.filter(i => i !== interest))
                                }>{interest}
                                </Tag>
                              )
                            })
                          }
                        </InlineStack>
                      ) : null
                    }
                  />
                }
              />
              <Select label="Gender" options={[
                'Any', 'Male', 'Female'
              ]} value={searchParams.gender}
                onChange={(value) => handleSetSearchParams('gender', value)} />
            </InlineGrid>
            <Divider />
            {
              selectedPlatform === 0 ? (
                <InlineGrid gap="300" columns="4">
                  <Select label="Engagement Rate" options={[
                    { label: 'Any', value: 'Any' },
                    { label: '>1%', value: '0.01' },
                    { label: '>2% (Average)', value: '0.02' },
                    { label: '>3%', value: '0.03' },
                    { label: '>4%', value: '0.04' },
                    { label: '>5%', value: '0.05' },
                    { label: '>6%', value: '0.06' },
                    { label: '>7%', value: '0.07' },
                    { label: '>8%', value: '0.08' },
                    { label: '>9%', value: '0.09' },
                  ]} value={searchParams.engagementRate} onChange={(value) => handleSetSearchParams('engagementRate', value)} />
                  <Select label="Fake Followers" options={[
                    { label: 'Any', value: 'Any' },
                    { label: '<25%', value: '25' },
                    { label: '<35%', value: '35' },
                    { label: '<50%', value: '50' },
                  ]} value={searchParams.fakeFollowers} onChange={(v) => handleSetSearchParams('fakeFollowers', v)} />
                </InlineGrid>
              ) : null
            }
            <InlineGrid alignItems="end" columns={{ xs: "1", md: "4" }} gap="300">
              <Select label="Sort By" options={[
                { label: 'Keyword Frequency', value: 'keywords' },
                { label: 'Followers', value: 'followers' },
                { label: 'Engagement Rate', value: 'engagement' },
              ]} />
              <Checkbox label="Has Contact Email" checked={searchParams.hasEmail} onChange={v => setSearchParams({ ...searchParams, hasEmail: v })} />
            </InlineGrid>
          </BlockStack>
        </Card>
        <Divider />
        <Text variant="headingLg">Audience</Text>
        <Card background="bg">
          <BlockStack gap="300">
            <InlineGrid columns={{ xs: "1", md: "4" }} gap="300">
              <Autocomplete
                allowMultiple
                onSelect={(value) => {
                  const newValue = value.pop()
                  handleSetSearchParams('audienceLocations', [...(searchParams.audienceLocations || []), newValue])
                  setWeights({
                    ...weights,
                    [newValue]: 0.1
                  })
                }}
                label="Country"
                options={
                  countries.map(c => ({ label: c, value: c })).filter(i => !(searchParams.audienceLocations?.includes(i.value) === true))
                }
                selected={searchParams.audienceLocations || []}
                textField={
                  <Autocomplete.TextField
                    placeholder={searchParams?.audienceLocations?.length > 0 ? "Add Location" : "Any"}
                    label="Locations"
                    value={''}
                    autoComplete="off"
                    verticalContent={
                      searchParams?.audienceLocations?.length > 0 ? (
                        <InlineStack gap="3" align="start">
                          {
                            searchParams.audienceLocations?.map((interest, i) => {
                              return (
                                <Tag key={i} onRemove={
                                  () => handleSetSearchParams('audienceLocations', searchParams.audienceLocations.filter(i => i !== interest))
                                }>{interest}
                                  <Select options={weightsList} value={weights[interest]} onChange={(value) => {
                                    setWeights({
                                      ...weights,
                                      [interest]: Number(value)
                                    })
                                  }} />
                                </Tag>
                              )
                            })
                          }
                        </InlineStack>
                      ) : null
                    }
                  />
                }
              />
              <Autocomplete
                allowMultiple
                onSelect={(value) => {
                  const newValue = value.pop()
                  handleSetSearchParams('audienceAges', [...(searchParams.audienceAges || []), newValue])
                  setWeights({
                    ...weights,
                    [newValue]: 0.1
                  })
                }}
                label="Ages"
                options={[
                  { label: 'Any', value: 'Any' },
                  { label: '13-17', value: '13-17' },
                  { label: '18-24', value: '18-24' },
                  { label: '25-34', value: '25-34' },
                  { label: '35-44', value: '35-44' },
                  { label: '45-64', value: '45-64' },
                  { label: '65+', value: '65-' }
                ].filter(i => !(searchParams.audienceAges?.includes(i.value) === true))}
                selected={searchParams.audienceAges || []}
                textField={
                  <Autocomplete.TextField
                    placeholder={searchParams?.audienceAges?.length > 0 ? "Add Age" : "Any"}
                    label="Ages"
                    value={''}
                    autoComplete="off"
                    verticalContent={
                      searchParams?.audienceAges?.length > 0 ? (
                        <InlineStack gap="3" align="start">
                          {
                            searchParams.audienceAges?.map(interest => {
                              return (
                                <Tag key={interest} onRemove={
                                  () => handleSetSearchParams('audienceAges', searchParams.audienceAges.filter(i => i !== interest))
                                }>{interest}
                                  <Select options={weightsList} value={weights[interest]} onChange={(value) => {
                                    setWeights({
                                      ...weights,
                                      [interest]: Number(value)
                                    })
                                  }} />
                                </Tag>
                              )
                            })
                          }
                        </InlineStack>
                      ) : null
                    }
                  />
                }
              />
              <Autocomplete
                onSelect={(value) => {
                  const newValue = value.pop()
                  handleSetSearchParams('audienceGender', newValue)
                  setWeights({
                    ...weights,
                    [newValue]: 0.1
                  })
                }}
                label="Gender"
                options={[
                  { label: 'Male', value: 'male' },
                  { label: 'Female', value: 'female' },
                ]}
                selected={searchParams.audienceGenders || []}
                textField={
                  <Autocomplete.TextField
                    placeholder={searchParams?.audienceGenders?.length > 0 ? "Add Gender" : "Any"}
                    label="Genders"
                    value={''}
                    autoComplete="off"
                    verticalContent={
                      searchParams?.audienceGender ? (
                        <InlineStack gap="3" align="start">
                          <Tag key={searchParams?.audienceGender} onRemove={
                            () => handleSetSearchParams('audienceGender', null)
                          }>{searchParams?.audienceGender}
                            <Select options={weightsList} value={weights[searchParams?.audienceGender]} onChange={(value) => {
                              setWeights({
                                ...weights,
                                [searchParams?.audienceGender]: Number(value)
                              })
                            }} />
                          </Tag>
                        </InlineStack>
                      ) : null
                    }
                  />
                }
              />
            </InlineGrid>
          </BlockStack>
        </Card>
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="300" blockAlign="center">
            <Button disabled={!credits} loading={searchLoading} size="large" variant="primary" onClick={() => handleSearch()}>Search</Button>
            {
              /*
              <Popover active={savedSearchesOpen} onClose={() => setSavedSearchesOpen(false)} activator={
                <Button variant="monochromePlain" disclosure onClick={() => setSavedSearchesOpen(true)}>
                  Saved Searches
                </Button>
              }>
                <ActionList actionRole="menuitem" items={[...savedSearches.map(search => {
                  return {
                    content: search.name,
                    onAction: () => {
                      setSavedSearch(search)
                      setSearchResults(search.results || [])
                      setSearchParams(search.state || {})
                      setWeights(search.weights || {})
                      setSavedSearchesOpen(false)
                    }
                  }
                }), {
                    content: 'Save Current Search',
                    onAction: () => {
                      setNewSavedSearchOpen({})
                      setSavedSearchesOpen(false)
                    }
                  }]} />
              </Popover>
  */
            }
          </InlineStack>
          <InlineStack gap="100">
            {
              searchResults?.lookalikes?.length ? (
                <Card>Showing {searchResults.lookalikes.length} of {searchResults?.total?.toLocaleString()} Results</Card>
              ) : searchResults ? (
                <Card>No Results</Card>
              ) : (
                null
              )
            }
          </InlineStack>
        </InlineStack>
        <BlockStack>
          <IndexTable
            selectable={false}
            resourceName={{
              singular: 'Creator',
              plural: 'Creators'
            }}
            promotedBulkActions={[
              {
                content: 'Get Selected Profiles',
                onAction: () => {
                  setConfirmSaveSelected(true)
                },
                disabled: !credits
              },
              /*
                {
                  content: 'Hide Selected',
                  onAction: () => {
                    setBulkHideOpen(true)
                  }
                }
              */
            ]}
            selectedItems={selectedSearchResults}
            selectedItemsCount={(selectedSearchResults?.length || 0)}
            onSelectionChange={(count, checked, userID) => {
              if (count === 'page') {
                setSelectedSearchResults(checked ? searchResults?.lookalikes?.map(i => i.userId) : [])
              } else if (checked) {
                if (leads?.find((lead) => lead.profile?.userId === userID)) {
                  setToast({
                    content: 'This creator is already saved',
                  })
                } else {
                  setSelectedSearchResults([...selectedSearchResults, userID])
                }
              } else {
                setSelectedSearchResults(selectedSearchResults.filter(i => i !== userID))
              }
            }}
            itemCount={rowMarkup?.length}
            verticalAlign="middle"
            headings={searchResults?.platform !== 'instagram' ? [
              { title: 'Name' },
              { title: 'Followers' },
              { title: 'Average Views' },
              { title: 'Engagement' },
              { title: 'Actions' }
            ] : [
              { title: 'Name' },
              { title: 'Followers' },
              { title: 'Engagement' },
              { title: 'Actions' }
            ]} >
            {rowMarkup}
          </IndexTable>
        </BlockStack>
        {
          searchResults && searchResults?.lookalikes?.length < searchResults.total ? (
            <InlineStack align="center">
              <Button loading={searchLoading} onClick={() => {
                handleSearch(Math.floor(searchResults?.lookalikes?.length / 15))
              }}>Load More</Button>
            </InlineStack>
          ) : (
            null
          )
        }
      </BlockStack>
    </AppLayout>
  )
}
